import { queryOptions, useQuery } from "@tanstack/react-query";
import { Models } from "@/api/types";
import { stripeLoader } from "./stripeLoader/stripeLoader";

export type WalletName = "Apple Pay" | "Google Pay";

export function getWalletName(
  methods: { applePay?: boolean; googlePay?: boolean } | null | undefined,
): WalletName | null {
  if (methods?.applePay) return "Apple Pay";
  if (methods?.googlePay) return "Google Pay";
  return null;
}

export function walletAvailabilityQuery(key: string | null, platform: Models<"PlatformModel">) {
  const country = platform.stripeRegion;
  let currency: "cad" | "usd" | null = null;
  if (country === "CA") currency = "cad";
  if (country === "US") currency = "usd";
  const onBehalfOf = platform.paymentProcessor?.paymentProcessorIdentifier;
  return queryOptions({
    queryKey: ["wallet-availability", platform.id, key, country, currency, onBehalfOf],
    enabled: Boolean(key && country && currency),
    queryFn: async () => {
      if (!key || !country || !currency)
        throw new Error("Wallet discovery requires a Stripe region and key");
      const stripe = await stripeLoader.getStripe(key);
      if (!stripe) throw new Error("Stripe is unavailable for wallet discovery");
      // This request is never shown or used to pay. The real Express Checkout Element owns payment.
      const request = stripe.paymentRequest({
        country,
        currency,
        total: { label: "Wallet availability", amount: 0 },
        disableWallets: ["link", "browserCard"],
        ...(onBehalfOf ? { onBehalfOf } : {}),
      });
      return getWalletName(await request.canMakePayment());
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useWalletAvailability(key: string | null, platform: Models<"PlatformModel">) {
  // A preflight result is a display hint; each live Element can override it for its intent.
  return useQuery(walletAvailabilityQuery(key, platform));
}
